// B4 — the term coupling graph (PRD 16 §5). *"Readers who look up `webhook`
// look up `idempotency key` within 40 seconds, 8x base rate."*
//
// The only panel on this screen computed from behaviour rather than corpus
// text, which makes it the one an entrant cannot reconstruct from a customer's
// documents — and the one where a false number is most persuasive. A lift is a
// ratio of a ratio: two rare terms that land in the same visit once by accident
// produce a lift in the hundreds and it looks like the strongest finding in the
// product.
//
// Five things here are load-bearing:
//
//  1. **Every lift is rendered beside the base rate it multiplies.** "8x" alone
//     is not a claim; "8x, where chance alone would give 4" is.
//  2. **Below the floor there is NO lift** — the backend's own reason, rendered
//     visibly, never "1x" and never "0". A lift of 1.0 already means "no
//     association", so a zero here would not be a weak reading, it would be an
//     impossible one.
//  3. **"Together", never "then".** The rollup keys the pair alphabetically and
//     stores an absolute gap, so nothing knows which term was opened first.
//  4. **"In one visit", stated on the panel.** A co-occurrence is two look-ups
//     in one tab. It is not one person and can never span two visits — there is
//     no reader identifier in this product and there is not going to be one.
//  5. **The gap is not a dwell.** It says how close together two look-ups were,
//     never how long either was read.

import { Card, EmptyState } from "../../ui";
import type { CoupledPair, TermCouplingReport } from "./api";
import { formatCount, formatDuration, shownOf } from "./format";
import {
  COUPLING_COUPLED,
  COUPLING_EMPTY_BODY,
  COUPLING_EMPTY_HEADLINE,
  COUPLING_EXPECTED_LABEL,
  COUPLING_GAP_WHY,
  COUPLING_INDEPENDENT,
  COUPLING_LIFT_WHY,
  COUPLING_NO_VERDICT_WHY,
  COUPLING_ONE_VISIT,
  COUPLING_SUBTITLE,
  COUPLING_SUPPRESSED_WHY,
  COUPLING_TITLE,
  COUPLING_TOGETHER_LABEL,
  SESSIONS_LABEL,
} from "./metricLabels";
import "./analytics.css";

const MAX_ROWS = 5;

/** "8.0x". One decimal — the underlying counts are small enough that a second
 *  would imply a precision the estimate does not have. Never called on a null:
 *  an absent lift renders the backend's reason instead. */
export function formatLift(value: number): string {
  return `${value.toFixed(1)}x`;
}

/** The display string, falling back to the `term_id` when the rollup knows an
 *  id the published document no longer does. The behaviour was real, so the row
 *  is kept rather than dropped. */
function label(name: string | null, termId: string): string {
  return name ?? termId;
}

function pairName(pair: CoupledPair): string {
  return `${label(pair.name_a, pair.term_a)} + ${label(pair.name_b, pair.term_b)}`;
}

export function TermCoupling({ report }: { report: TermCouplingReport }) {
  const shown = report.pairs.slice(0, MAX_ROWS);
  const where = report.doc_title ?? report.doc_id;

  return (
    <Card
      header={COUPLING_TITLE}
      footer={
        <span className="type-bodyS">
          {report.pairs.length === 0
            ? COUPLING_SUBTITLE
            : `${shownOf(shown.length, report.pairs_found, "pair")} on ${where} · ${report.pairs_measured} measured · ${COUPLING_ONE_VISIT}`}
        </span>
      }
    >
      {report.pairs.length === 0 ? (
        <EmptyState
          headline={COUPLING_EMPTY_HEADLINE}
          body={COUPLING_EMPTY_BODY}
        />
      ) : (
        <>
          {/* The pairs that are NOT on this list. Dropped by the API before a
           *  row existed, so the panel can only report how many — which is
           *  the point, and is also what stops a short list reading as a
           *  complete one. */}
          {report.pairs_suppressed > 0 ? (
            <p className="maisight-analytics-coupling-note type-bodyS">
              {`${formatCount(report.pairs_suppressed)} more pairs are not shown. ${COUPLING_SUPPRESSED_WHY}`}
            </p>
          ) : null}
          <ul
            className="maisight-analytics-meters"
            aria-label="Terms opened together in one visit"
          >
            {shown.map((pair) => {
              // Narrowed on the field itself, not through a boolean: a `?? 0`
              // fallback anywhere near this number could render "0.0x", and a
              // lift of 1.0 already means "no association" — 0 would not be a
              // weak reading, it would be an impossible one.
              const lift = pair.lift;
              const measured = lift !== null;
              return (
                <li
                  key={`${pair.term_a}|${pair.term_b}`}
                  className="maisight-analytics-meter-row"
                >
                  <div className="maisight-analytics-meter-head">
                    <span className="maisight-analytics-term type-numSm">
                      {pairName(pair)}
                    </span>
                    <span
                      className="maisight-analytics-metric type-numSm"
                      title={measured ? COUPLING_LIFT_WHY : undefined}
                      data-measured={measured ? "yes" : "no"}
                    >
                      {lift !== null ? formatLift(lift) : "rate not measured"}
                    </span>
                  </div>
                  <p className="maisight-analytics-coupling-meta type-bodyS">
                    {measured ? (
                      <>
                        {/* The base rate the multiple is a multiple OF. Without
                         *  it "8x" is a number with no referent. */}
                        <span>
                          {`${formatCount(pair.sessions_both)} ${SESSIONS_LABEL} ${COUPLING_TOGETHER_LABEL}, ${COUPLING_EXPECTED_LABEL} ${pair.expected_together?.toFixed(1)}`}
                        </span>
                        <span className="maisight-analytics-coupling-verdict">
                          {pair.verdict === "coupled"
                            ? COUPLING_COUPLED
                            : COUPLING_INDEPENDENT}
                        </span>
                      </>
                    ) : (
                      <span
                        className="maisight-analytics-metric"
                        data-measured="no"
                      >
                        {`${COUPLING_NO_VERDICT_WHY} ${pair.not_measured_reason ?? ""}`}
                      </span>
                    )}
                    {/* "Together within", never "then within" — the pairing
                     *  carries no order. */}
                    {pair.median_gap_ms !== null ? (
                      <span title={COUPLING_GAP_WHY}>
                        {`typically opened together within ${formatDuration(pair.median_gap_ms)}`}
                      </span>
                    ) : null}
                  </p>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </Card>
  );
}
